import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { partnersApi, Partner, PartnerStats, PartnerStatsParams } from '../../api/partners';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Loader2, MousePointerClick, Users, Globe } from 'lucide-react';
import { toast } from 'sonner';

interface PartnerStatsPageProps {
  isDark: boolean;
}

const PLACEMENT_COLORS: Record<string, string> = {
  landing: '#3b82f6',
  dashboard: '#10b981',
  footer: '#a855f7',
  all: '#64748b',
};

const PRESETS: Array<{ label: string; days: number }> = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

export default function PartnerStatsPage({ isDark }: PartnerStatsPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [stats, setStats] = useState<PartnerStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<PartnerStatsParams>(() => {
    const to = new Date();
    const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10),
      groupBy: 'day',
    };
  });

  const load = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [p, s] = await Promise.all([
        partnersApi.getById(id),
        partnersApi.getStats(id, filters),
      ]);
      setPartner(p);
      setStats(s);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load stats');
    } finally {
      setIsLoading(false);
    }
  }, [id, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const applyPreset = (days: number) => {
    const to = new Date();
    const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
    setFilters((f) => ({
      ...f,
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10),
    }));
  };

  if (isLoading || !stats || !partner) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0EA5A4]" />
      </div>
    );
  }

  const timeseriesData = stats.timeseries.map((t) => ({
    date: format(new Date(t.date), 'MMM d'),
    clicks: t.clicks,
  }));

  const placementData = stats.by_placement.map((p) => ({
    name: p.placement,
    value: p.clicks,
  }));

  const kpis = [
    { label: 'Total clicks', value: stats.total_clicks, icon: MousePointerClick, color: 'text-[#0EA5A4]' },
    { label: 'Unique users', value: stats.unique_users, icon: Users, color: 'text-blue-500' },
    { label: 'Unique visitors', value: stats.unique_visitors, icon: Globe, color: 'text-emerald-500' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/partners')}
          className={cn(isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : '')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />Back
        </Button>
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#0F172A]')}>
            {partner.name}
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Click analytics · slug: <span className="font-mono">{partner.slug}</span>
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
        <div className="p-4 grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div className="space-y-2">
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>From</Label>
            <Input
              type="date"
              value={filters.from || ''}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}
            />
          </div>
          <div className="space-y-2">
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>To</Label>
            <Input
              type="date"
              value={filters.to || ''}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}
            />
          </div>
          <div className="space-y-2">
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Group by</Label>
            <Select
              value={filters.groupBy || 'day'}
              onValueChange={(value) => setFilters((f) => ({ ...f, groupBy: value as 'day' | 'week' | 'month' }))}
            >
              <SelectTrigger className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Day</SelectItem>
                <SelectItem value="week">Week</SelectItem>
                <SelectItem value="month">Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2 md:col-span-2">
            {PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant="outline"
                size="sm"
                onClick={() => applyPreset(preset.days)}
                className={cn(isDark ? 'border-slate-600 text-white hover:bg-slate-700' : '')}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-6 flex items-center justify-between">
              <div>
                <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>{kpi.label}</p>
                <p className={cn('text-3xl font-bold mt-1 tabular-nums', isDark ? 'text-white' : 'text-[#0F172A]')}>
                  {kpi.value.toLocaleString()}
                </p>
              </div>
              <kpi.icon className={cn('w-10 h-10', kpi.color)} />
            </div>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className={cn('lg:col-span-2', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
          <div className="p-6">
            <h2 className={cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#0F172A]')}>
              Clicks over time
            </h2>
            {timeseriesData.length === 0 ? (
              <p className={cn('text-sm py-12 text-center', isDark ? 'text-slate-400' : 'text-slate-500')}>
                No clicks in this period yet
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={timeseriesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#475569' : '#e2e8f0'} />
                  <XAxis dataKey="date" tick={{ fill: isDark ? '#94a3b8' : '#64748b' }} />
                  <YAxis tick={{ fill: isDark ? '#94a3b8' : '#64748b' }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? '#1e293b' : '#ffffff',
                      border: isDark ? '1px solid #475569' : '1px solid #e2e8f0',
                      color: isDark ? '#ffffff' : '#0f172a',
                    }}
                  />
                  <Line type="monotone" dataKey="clicks" stroke="#0EA5A4" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
          <div className="p-6">
            <h2 className={cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#0F172A]')}>
              By placement
            </h2>
            {placementData.length === 0 ? (
              <p className={cn('text-sm py-12 text-center', isDark ? 'text-slate-400' : 'text-slate-500')}>
                No data
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={placementData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={(entry: any) => `${entry.name}: ${entry.value}`}
                  >
                    {placementData.map((entry) => (
                      <Cell key={entry.name} fill={PLACEMENT_COLORS[entry.name] || '#64748b'} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? '#1e293b' : '#ffffff',
                      border: isDark ? '1px solid #475569' : '1px solid #e2e8f0',
                      color: isDark ? '#ffffff' : '#0f172a',
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      {/* Placement table */}
      <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
        <div className="p-6">
          <h2 className={cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#0F172A]')}>
            Placement breakdown
          </h2>
          {stats.by_placement.length === 0 ? (
            <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>No clicks yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={placementData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#475569' : '#e2e8f0'} />
                <XAxis dataKey="name" tick={{ fill: isDark ? '#94a3b8' : '#64748b' }} />
                <YAxis tick={{ fill: isDark ? '#94a3b8' : '#64748b' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? '#1e293b' : '#ffffff',
                    border: isDark ? '1px solid #475569' : '1px solid #e2e8f0',
                    color: isDark ? '#ffffff' : '#0f172a',
                  }}
                />
                <Bar dataKey="value" fill="#0EA5A4" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
    </div>
  );
}
